// frontend/src/services/apiService.js

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/scraper';
const TIMEOUT_MS = 30000;

const ERROR_MESSAGES = {
    NETWORK: 'Network error. Please check your connection.',
    TIMEOUT: 'Request timed out. Please try again.',
    SERVER: 'Server error. Please try again later.',
    VALIDATION: 'Invalid input data.',
    UNAUTHORIZED: 'Unauthorized access.',
    NOT_FOUND: 'Resource not found.',
};

// Input validation helpers
const validateSearchTerms = (terms) => {
    if (!Array.isArray(terms) || terms.length === 0) {
        throw new Error('At least one search term is required');
    }
    if (terms.some(term => typeof term !== 'string' || !term.trim())) {
        throw new Error('All search terms must be non-empty strings');
    }
    return terms.map(term => term.trim());
};

const validateLocations = (locations) => {
    if (!Array.isArray(locations) || locations.length === 0) {
        throw new Error('At least one location is required');
    }
    if (locations.some(location => typeof location !== 'string' || !location.trim())) {
        throw new Error('All locations must be non-empty strings');
    }
    return locations.map(location => location.trim());
};

const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const fetchWithTimeout = async (resource, options = {}) => {
    const controller = new AbortController();
    const customTimeout = options.customTimeout || TIMEOUT_MS;
    const timeoutId = setTimeout(() => controller.abort(), customTimeout);

    try {
        const response = await fetch(resource, {
            ...options,
            signal: controller.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...getAuthHeader(),
                ...options.headers,
            }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            
            if (response.status === 401) {
                // Token expired or invalid
                localStorage.removeItem('token');
                window.location.reload(); // Force re-login
                throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
            }

            // Enhanced error handling with status codes
            switch (response.status) {
                case 400:
                    throw new Error(errorData?.error || errorData?.detail || ERROR_MESSAGES.VALIDATION);
                case 404:
                    throw new Error(ERROR_MESSAGES.NOT_FOUND);
                case 500:
                    throw new Error(ERROR_MESSAGES.SERVER);
                default:
                    throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
            }
        }
        
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error(ERROR_MESSAGES.TIMEOUT);
        }
        if (error.message === 'Failed to fetch') {
            throw new Error(ERROR_MESSAGES.NETWORK);
        }
        throw error;
    }
};

export const apiService = {
    // Auth Methods
    login: async (email, password) => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });
            
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Login failed');
            
            if (data.token) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
            }
            return data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    },

    googleLogin: async (token) => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/google`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token })
            });
            
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Google Login failed');
            
            if (data.token) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
            }
            return data;
        } catch (error) {
            console.error('Google Login error:', error);
            throw error;
        }
    },

    register: async (email, password, name) => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password, name })
            });
            
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Registration failed');
            
            // If approval is required, we don't get a token
            if (data.require_approval) {
                return {
                    require_approval: true,
                    message: data.message
                };
            }

            // Fallback for older behavior if any
            if (data.token) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
            }
            return data;
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    },

    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    },

    checkServerStatus: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/status`);
            return await response.json();
        } catch (error) {
            console.error('Server status check failed:', error);
            return {
                available: false,
                dbConnected: false,
                error: error.message
            };
        }
    },

    startScraping: async (searchTerms, locations, platforms = ['linkedin'], force = false) => {
        try {
            // Validate inputs
            const validatedTerms = validateSearchTerms(searchTerms);
            const validatedLocations = validateLocations(locations);

            const response = await fetchWithTimeout(`${API_BASE_URL}/start`, {
                method: 'POST',
                body: JSON.stringify({ 
                    search_terms: validatedTerms, 
                    locations: validatedLocations,
                    platforms: platforms,
                    force: force
                })
            });
            return await response.json();
        } catch (error) {
            console.error('Error starting scraper:', error);
            throw error;
        }
    },

    stopScraping: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/stop`, {
                method: 'POST'
            });
            return await response.json();
        } catch (error) {
            console.error('Error stopping scraper:', error);
            throw error;
        }
    },

    getStats: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/stats`);
            const data = await response.json();
            return {
                profiles_found: parseInt(data.profiles_found) || 0,
                pages_scraped: parseInt(data.pages_scraped) || 0,
                successful_requests: parseInt(data.successful_requests) || 0,
                failed_requests: parseInt(data.failed_requests) || 0,
                progress: parseFloat(data.progress) || 0,
                is_running: Boolean(data.is_running),
                error: data.error || null,
                profiles_in_db: parseInt(data.profiles_in_db) || 0,
                last_update: data.last_update || new Date().toISOString(),
                current_query: data.current_query || "Idle"
            };
        } catch (error) {
            console.error('Error fetching stats:', error);
            return {
                profiles_found: 0,
                pages_scraped: 0,
                successful_requests: 0,
                failed_requests: 0,
                progress: 0,
                is_running: false,
                error: error.message,
                profiles_in_db: 0,
                last_update: new Date().toISOString()
            };
        }
    },

    getResults: async (limit = 1000, offset = 0) => {
        try {
            const response = await fetchWithTimeout(
                `${API_BASE_URL}/results?limit=${limit}&offset=${offset}`
            );
            const data = await response.json();
            if (!data.data?.profiles) {
                throw new Error('Invalid results format');
            }
            return data.data;
        } catch (error) {
            console.error('Error fetching results:', error);
            throw error;
        }
    },

    exportResults: async (format = 'csv', options = {}) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/export`, {
                method: 'POST',
                body: JSON.stringify({ format, options })
            });
            
            if (format === 'json') {
                return await response.json();
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `scraping_results_${new Date().toISOString()}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            return true;
        } catch (error) {
            console.error('Error exporting results:', error);
            throw error;
        }
    },

    deleteProfile: async (email) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/profile/${email}`, {
                method: 'DELETE'
            });
            return await response.json();
        } catch (error) {
            console.error('Error deleting profile:', error);
            throw error;
        }
    },

    getDbStats: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/db/stats`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching DB stats:', error);
            throw error;
        }
    },

    // Email Campaign API Methods
    getEmailStats: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/email/stats`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching email stats:', error);
            throw error;
        }
    },

    previewEmail: async (profileId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/email/preview`, {
                method: 'POST',
                body: JSON.stringify({ profile_id: profileId })
            });
            return await response.json();
        } catch (error) {
            console.error('Error previewing email:', error);
            throw error;
        }
    },

    sendEmailCampaign: async (campaignName, limit = null, testMode = false) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/email/send-campaign`, {
                method: 'POST',
                body: JSON.stringify({
                    campaign_name: campaignName,
                    limit: limit,
                    test_mode: testMode
                }),
                customTimeout: 300000 // 5 minutes for AI email generation
            });
            return await response.json();
        } catch (error) {
            console.error('Error sending email campaign:', error);
            throw error;
        }
    },

    getUnsentProfiles: async (limit = 100) => {
        try {
            const response = await fetchWithTimeout(
                `${API_BASE_URL}/email/unsent-profiles?limit=${limit}`
            );
            return await response.json();
        } catch (error) {
            console.error('Error fetching unsent profiles:', error);
            throw error;
        }
    },

    sendTestEmail: async (testEmail) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/email/test`, {
                method: 'POST',
                body: JSON.stringify({ test_email: testEmail })
            });
            return await response.json();
        } catch (error) {
            console.error('Error sending test email:', error);
            throw error;
        }
    },

    // Inbox API Methods
    checkInbox: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/check`, {
                method: 'POST'
            });
            return await response.json();
        } catch (error) {
            console.error('Error checking inbox:', error);
            throw error;
        }
    },

    startInboxMonitoring: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/start-monitoring`, {
                method: 'POST'
            });
            return await response.json();
        } catch (error) {
            console.error('Error starting inbox monitoring:', error);
            throw error;
        }
    },

    stopInboxMonitoring: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/stop-monitoring`, {
                method: 'POST'
            });
            return await response.json();
        } catch (error) {
            console.error('Error stopping inbox monitoring:', error);
            throw error;
        }
    },

    getReplies: async (unreadOnly = false, intent = null, limit = 100) => {
        try {
            let url = `${API_BASE_URL.replace('/scraper', '')}/inbox/replies?limit=${limit}`;
            if (unreadOnly) url += '&unread_only=true';
            if (intent) url += `&intent=${intent}`;

            const response = await fetchWithTimeout(url);
            return await response.json();
        } catch (error) {
            console.error('Error fetching replies:', error);
            throw error;
        }
    },

    getReplyDetails: async (replyId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/reply/${replyId}`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching reply details:', error);
            throw error;
        }
    },

    markReplyRead: async (replyId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/reply/${replyId}/mark-read`, {
                method: 'PUT'
            });
            return await response.json();
        } catch (error) {
            console.error('Error marking reply as read:', error);
            throw error;
        }
    },

    sendResponseToReply: async (replyId, responseBody, responseType = 'custom') => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/reply/${replyId}/respond`, {
                method: 'POST',
                body: JSON.stringify({
                    response_body: responseBody,
                    response_type: responseType
                })
            });
            return await response.json();
        } catch (error) {
            console.error('Error sending response:', error);
            throw error;
        }
    },

    // CRM API Methods
    getPipelineStats: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/pipeline/stats`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching pipeline stats:', error);
            throw error;
        }
    },

    getLeads: async (stage = null, limit = 100) => {
        try {
            let url = `${API_BASE_URL.replace('/scraper', '')}/crm/leads?limit=${limit}`;
            if (stage) url += `&stage=${stage}`;

            const response = await fetchWithTimeout(url);
            return await response.json();
        } catch (error) {
            console.error('Error fetching leads:', error);
            throw error;
        }
    },

    getLeadDetails: async (leadId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/lead/${leadId}`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching lead details:', error);
            throw error;
        }
    },

    updateLeadStage: async (leadId, stage, reason = null) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/lead/${leadId}/stage`, {
                method: 'PUT',
                body: JSON.stringify({ stage, reason })
            });
            return await response.json();
        } catch (error) {
            console.error('Error updating lead stage:', error);
            throw error;
        }
    },

    updateLeadValue: async (leadId, dealValue) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/lead/${leadId}/value`, {
                method: 'PUT',
                body: JSON.stringify({ deal_value: dealValue })
            });
            return await response.json();
        } catch (error) {
            console.error('Error updating lead value:', error);
            throw error;
        }
    },

    addLeadNote: async (leadId, note) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/lead/${leadId}/note`, {
                method: 'POST',
                body: JSON.stringify({ note })
            });
            return await response.json();
        } catch (error) {
            console.error('Error adding lead note:', error);
            throw error;
        }
    },

    getTasks: async (daysAhead = 7) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/tasks?days_ahead=${daysAhead}`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching tasks:', error);
            throw error;
        }
    },

    createTask: async (taskData) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/task`, {
                method: 'POST',
                body: JSON.stringify(taskData)
            });
            return await response.json();
        } catch (error) {
            console.error('Error creating task:', error);
            throw error;
        }
    },

    completeTask: async (taskId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/task/${taskId}/complete`, {
                method: 'PUT'
            });
            return await response.json();
        } catch (error) {
            console.error('Error completing task:', error);
            throw error;
        }
    },

    searchLeads: async (query, limit = 50) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/search?q=${encodeURIComponent(query)}&limit=${limit}`);
            return await response.json();
        } catch (error) {
            console.error('Error searching leads:', error);
            throw error;
        }
    },

    // Settings API Methods
    getAllSettings: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/settings`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching settings:', error);
            throw error;
        }
    },

    getSetting: async (settingKey) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/settings/${settingKey}`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching setting:', error);
            throw error;
        }
    },

    saveSetting: async (settingKey, settingValue, encrypted = false, description = '') => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/settings`, {
                method: 'POST',
                body: JSON.stringify({
                    setting_key: settingKey,
                    setting_value: settingValue,
                    encrypted,
                    description
                })
            });
            return await response.json();
        } catch (error) {
            console.error('Error saving setting:', error);
            throw error;
        }
    },

    deleteSetting: async (settingKey) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/settings/${settingKey}`, {
                method: 'DELETE'
            });
            return await response.json();
        } catch (error) {
            console.error('Error deleting setting:', error);
            throw error;
        }
    },

    getEmailConfig: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/settings/email-config`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching email config:', error);
            throw error;
        }
    },

    saveEmailConfig: async (config) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/settings/email-config`, {
                method: 'POST',
                body: JSON.stringify(config)
            });
            return await response.json();
        } catch (error) {
            console.error('Error saving email config:', error);
            throw error;
        }
    },

    getCampaignContext: async () => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/settings/campaign-context`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching campaign context:', error);
            throw error;
        }
    },

    saveCampaignContext: async (config) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/settings/campaign-context`, {
                method: 'POST',
                body: JSON.stringify(config)
            });
            return await response.json();
        } catch (error) {
            console.error('Error saving campaign context:', error);
            throw error;
        }
    },

    // ── Multi-mailbox: connected accounts + central inbox ──────────────────
    getEmailAccounts: async () => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/accounts`);
        return await response.json();
    },

    addEmailAccount: async (data) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/accounts`, {
            method: 'POST',
            body: JSON.stringify(data),
            customTimeout: 60000
        });
        return await response.json();
    },

    updateEmailAccount: async (accountId, data) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/accounts/${accountId}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
        return await response.json();
    },

    deleteEmailAccount: async (accountId) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/accounts/${accountId}`, {
            method: 'DELETE'
        });
        return await response.json();
    },

    getCockpit: async () => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/cockpit`);
        return await response.json();
    },

    getInboxMessages: async ({ accountId = null, direction = null, q = '', unreadOnly = false, includeNoise = false, limit = 100 } = {}) => {
        let url = `${API_BASE_URL.replace('/scraper', '')}/inbox/messages?limit=${limit}`;
        if (accountId) url += `&account_id=${accountId}`;
        if (direction) url += `&direction=${direction}`;
        if (q) url += `&q=${encodeURIComponent(q)}`;
        if (unreadOnly) url += '&unread_only=1';
        if (includeNoise) url += '&include_noise=1';
        const response = await fetchWithTimeout(url);
        return await response.json();
    },

    syncMailboxes: async () => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/sync`, {
            method: 'POST',
            customTimeout: 120000
        });
        return await response.json();
    },

    /* ── Records browser ─────────────────────────────────────── */
    getRecords: async (params = {}) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) qs.set(k, v); });
        const response = await fetchWithTimeout(`${API_BASE_URL}/db/profiles?${qs.toString()}`);
        return await response.json();
    },

    getRecordFull: async (id) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/records/profile/${id}/full`);
        return await response.json();
    },

    bulkRecords: async (ids, action) => {
        const response = await fetchWithTimeout(`${API_BASE_URL}/db/profiles/bulk`, {
            method: 'POST',
            body: JSON.stringify({ ids, action }),
        });
        return await response.json();
    },

    exportRecordsCsv: async (params = {}) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) qs.set(k, v); });
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API_BASE_URL}/db/profiles/export?${qs.toString()}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wnleads_records_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    },

    /* ── Dashboard / Mailboxes / Demos ───────────────────────── */
    getDashboardSummary: async () => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/dashboard/summary`);
        return await response.json();
    },

    getMailboxSummary: async () => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/accounts/summary`);
        return await response.json();
    },

    testMailbox: async (id) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/accounts/${id}/test`, {
            method: 'POST',
            customTimeout: 90000,
        });
        return await response.json();
    },

    updateMailbox: async (id, data) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/inbox/accounts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        return await response.json();
    },

    getDemos: async () => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/demos`);
        return await response.json();
    },

    getPendingDemos: async () => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/demos/pending`);
        return await response.json();
    },

    approveDemo: async (id, subject, body) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/demos/${id}/approve`, {
            method: 'POST',
            body: JSON.stringify({ subject, body }),
        });
        return await response.json();
    },

    discardDemo: async (id) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/demos/${id}/discard`, {
            method: 'POST',
            body: JSON.stringify({}),
        });
        return await response.json();
    },

    undeployDemo: async (id) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/crm/demos/${id}/undeploy`, {
            method: 'POST',
            body: JSON.stringify({}),
        });
        return await response.json();
    },

    /* ── Lead collector ──────────────────────────────────────── */
    getLeadStats: async () => {
        const response = await fetchWithTimeout(`${API_BASE_URL}/leads/stats`);
        return await response.json();
    },

    getLeadStatus: async () => {
        const response = await fetchWithTimeout(`${API_BASE_URL}/leads/status`);
        return await response.json();
    },

    getLeadAreas: async () => {
        const response = await fetchWithTimeout(`${API_BASE_URL}/leads/areas`);
        return await response.json();
    },

    runLeadsGoogle: async (queries, area) => {
        const response = await fetchWithTimeout(`${API_BASE_URL}/leads/google`, {
            method: 'POST',
            body: JSON.stringify({ queries, area }),
            customTimeout: 60000,
        });
        return await response.json();
    },

    runLeadsOsm: async (areas, categories) => {
        const response = await fetchWithTimeout(`${API_BASE_URL}/leads/osm`, {
            method: 'POST',
            body: JSON.stringify({ areas, categories }),
            customTimeout: 60000,
        });
        return await response.json();
    },

    runLeadsEnrich: async (kind, limit = 100) => {
        const response = await fetchWithTimeout(`${API_BASE_URL}/leads/${kind}`, {
            method: 'POST',
            body: JSON.stringify({ limit }),
            customTimeout: 60000,
        });
        return await response.json();
    },

    /* ── Demo follow-ups ─────────────────────────────────────── */
    getFollowupDemos: async () => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/followups/demos`);
        return await response.json();
    },

    getFollowupThread: async (email) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/followups/thread?email=${encodeURIComponent(email)}`);
        return await response.json();
    },

    setFollowupStatus: async (email, status) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/followups/status`, {
            method: 'POST',
            body: JSON.stringify({ email, status }),
        });
        return await response.json();
    },

    getFollowupDraft: async (email) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/followups/draft?email=${encodeURIComponent(email)}`);
        return await response.json();
    },

    generateFollowup: async (email, context = '') => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/followups/generate`, {
            method: 'POST',
            body: JSON.stringify({ email, context }),
            customTimeout: 120000,
        });
        return await response.json();
    },

    getAnalytics: async (days = 30) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/analytics/overview?days=${days}`);
        return await response.json();
    },

    markFollowupLost: async (email) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/followups/lost`, {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
        return await response.json();
    },

    sendFollowup: async (email, subject, body) => {
        const response = await fetchWithTimeout(`${API_BASE_URL.replace('/scraper', '')}/followups/send`, {
            method: 'POST',
            body: JSON.stringify({ email, subject, body }),
            customTimeout: 90000,
        });
        return await response.json();
    }
};
